import React, { useEffect, useMemo, useRef, useState } from 'react';
import FormFieldV2 from './FormFieldV2';
import useSubmitLead from '../../hooks/useSubmitLead';

const COPY = {
  en: {
    heading: 'Business Systems Audit — request the conversation',
    intro: 'Tell us a bit about your business and the workflow challenges you\'re facing. Sergio will reach out within one business day to schedule the audit conversation.',
    required_note: 'Fields marked with * are required.',
    labels: {
      contact_name: 'Your name',
      business_name: 'Business name',
      email: 'Work email',
      primary_challenge: 'Primary workflow or operational challenge',
      preferred_language: 'Preferred language for conversation',
      contact_consent: 'illumi.dev may contact me about this audit request.',
      privacy: 'See our privacy policy.',
      phone: 'Phone (optional)',
      role: 'Your role',
      employees: 'Number of employees',
      locations: 'Number of locations',
      current_tools: 'Current software or tools',
      business_impact: 'Business impact today',
      bilingual_required: 'Bilingual (EN/ES) workflows required?',
      timeline: 'Desired timeline',
      investment_range: 'Expected systems investment during the next 90 days',
      investment_hint: 'This covers the broader systems initiative — not the standalone audit price.',
      additional_context: 'Anything else we should know?',
      submit: 'Request the audit conversation',
      submitting: 'Sending…',
      lang_en: 'English',
      lang_es: 'Español',
      bilingual_yes: 'Yes',
      bilingual_no: 'No',
      bilingual_unsure: 'Not sure',
      timeline_30: 'Within 30 days',
      timeline_90: '30–90 days',
      timeline_later: '90+ days',
      timeline_explore: 'Exploring',
      emp_small: 'Fewer than 10',
      emp_mid: '10–50',
      emp_large: '50–200',
      emp_enterprise: '200+',
      invest_a: 'Under $2,000',
      invest_b: '$2,000–$5,000',
      invest_c: '$5,000–$15,000',
      invest_d: '$15,000+',
      invest_none: 'Not sure yet',
      pick: 'Select an option',
    },
    success: {
      heading: 'Your audit request is in.',
      body: (name, biz) => `Thanks ${name}. We received your request about ${biz}. Sergio will reach out within one business day to schedule the Business Systems Audit conversation.`,
      ref: (id) => `Reference: ${id}`,
      back: 'Return to overview',
    },
    errors: {
      required: 'This field is required.',
      email: 'Please enter a valid email address.',
      consent: 'Please acknowledge the contact consent.',
      generic: 'Something went wrong. Please try again.',
      summary: 'Please fix the highlighted fields.',
    },
  },
  es: {
    heading: 'Auditoría de Sistemas de Negocio — solicita la conversación',
    intro: 'Cuéntanos brevemente sobre tu negocio y los retos operativos actuales. Sergio te contactará en un día hábil para agendar la conversación de auditoría.',
    required_note: 'Los campos marcados con * son obligatorios.',
    labels: {
      contact_name: 'Tu nombre',
      business_name: 'Nombre del negocio',
      email: 'Correo de trabajo',
      primary_challenge: 'Reto principal de flujo o de operación',
      preferred_language: 'Idioma preferido para la conversación',
      contact_consent: 'Autorizo a illumi.dev a contactarme sobre esta solicitud.',
      privacy: 'Consulta nuestra política de privacidad.',
      phone: 'Teléfono (opcional)',
      role: 'Tu rol',
      employees: 'Cantidad de empleados',
      locations: 'Cantidad de ubicaciones',
      current_tools: 'Herramientas o software actuales',
      business_impact: 'Impacto en el negocio hoy',
      bilingual_required: '¿Requieren flujos bilingües (EN/ES)?',
      timeline: 'Cronograma deseado',
      investment_range: 'Inversión estimada en sistemas durante los próximos 90 días',
      investment_hint: 'Se refiere a la iniciativa completa de sistemas — no al precio de la auditoría en sí.',
      additional_context: '¿Algo más que debamos saber?',
      submit: 'Solicitar la conversación',
      submitting: 'Enviando…',
      lang_en: 'English',
      lang_es: 'Español',
      bilingual_yes: 'Sí',
      bilingual_no: 'No',
      bilingual_unsure: 'No estoy seguro',
      timeline_30: 'En 30 días',
      timeline_90: '30–90 días',
      timeline_later: 'Más de 90 días',
      timeline_explore: 'Explorando',
      emp_small: 'Menos de 10',
      emp_mid: '10–50',
      emp_large: '50–200',
      emp_enterprise: '200+',
      invest_a: 'Menos de $2,000',
      invest_b: '$2,000–$5,000',
      invest_c: '$5,000–$15,000',
      invest_d: '$15,000+',
      invest_none: 'Aún no lo sé',
      pick: 'Selecciona una opción',
    },
    success: {
      heading: 'Recibimos tu solicitud de auditoría.',
      body: (name, biz) => `Gracias ${name}. Registramos tu solicitud sobre ${biz}. Sergio te contactará en un día hábil para agendar la conversación de Auditoría de Sistemas de Negocio.`,
      ref: (id) => `Referencia: ${id}`,
      back: 'Volver a la vista general',
    },
    errors: {
      required: 'Este campo es obligatorio.',
      email: 'Ingresa un correo válido.',
      consent: 'Debes aceptar el permiso de contacto.',
      generic: 'Algo salió mal. Intenta de nuevo.',
      summary: 'Corrige los campos resaltados.',
    },
  },
};

const EMPTY = {
  contact_name: '', business_name: '', email: '', primary_challenge: '',
  preferred_language: 'en', contact_consent: false,
  phone: '', role: '', employees: '', locations: '', current_tools: '',
  business_impact: '', bilingual_required: '', timeline: '', investment_range: '',
  additional_context: '', website: '',
};

const AuditFormV2 = ({ lang = 'en' }) => {
  const t = COPY[lang] || COPY.en;
  const [values, setValues] = useState(() => ({ ...EMPTY, preferred_language: lang === 'es' ? 'es' : 'en' }));
  const [errors, setErrors] = useState({});
  const mountedAtRef = useRef(Date.now());
  const successHeadingRef = useRef(null);
  const preferredLangTouchedRef = useRef(false);
  const { submit, pending, status, error, response, reset } = useSubmitLead('/api/submissions/audit');

  // Move focus to success heading when the response arrives
  useEffect(() => {
    if (status === 'success' && successHeadingRef.current) {
      successHeadingRef.current.focus();
    }
  }, [status]);

  // Sync preferred_language with URL ?lang= when user hasn't manually changed the field.
  useEffect(() => {
    if (preferredLangTouchedRef.current) return;
    const next = lang === 'es' ? 'es' : 'en';
    setValues((s) => (s.preferred_language === next ? s : { ...s, preferred_language: next }));
  }, [lang]);

  const setField = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    if (k === 'preferred_language') preferredLangTouchedRef.current = true;
    setValues((s) => ({ ...s, [k]: v }));
    if (errors[k]) setErrors((s) => ({ ...s, [k]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!values.contact_name.trim()) e.contact_name = t.errors.required;
    if (!values.business_name.trim()) e.business_name = t.errors.required;
    if (!values.email.trim()) e.email = t.errors.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) e.email = t.errors.email;
    if (!values.primary_challenge.trim()) e.primary_challenge = t.errors.required;
    if (!values.contact_consent) e.contact_consent = t.errors.consent;
    return e;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (pending) return;
    const v = validate();
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    const payload = {
      ...values,
      email: values.email.trim(),
      contact_name: values.contact_name.trim(),
      business_name: values.business_name.trim(),
      primary_challenge: values.primary_challenge.trim(),
      elapsed_ms: Date.now() - mountedAtRef.current,
    };
    await submit(payload);
  };

  const shortRef = useMemo(() => (response?.submission_id || '').slice(0, 8), [response]);

  if (status === 'success') {
    const name = values.contact_name.trim() || (lang === 'es' ? 'de nuevo' : 'again');
    const biz = values.business_name.trim();
    return (
      <div className="iv2-audit-success" data-testid="iv2-audit-success">
        <div className="iv2-container">
          <div aria-live="polite" role="status" className="iv2-audit-success-card">
            <h2
              ref={successHeadingRef}
              tabIndex={-1}
              className="iv2-audit-success-heading"
              data-testid="iv2-audit-success-heading"
            >
              {t.success.heading}
            </h2>
            <p className="iv2-audit-success-body">{t.success.body(name, biz)}</p>
            {shortRef && (
              <p className="iv2-audit-success-ref" data-testid="iv2-audit-success-ref">
                {t.success.ref(shortRef)}
              </p>
            )}
            <a href="/v2" className="iv2-btn iv2-btn-secondary" data-testid="iv2-audit-back">
              {t.success.back}
            </a>
          </div>
        </div>
      </div>
    );
  }

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <form className="iv2-audit-form" onSubmit={onSubmit} noValidate data-testid="iv2-audit-form">
      <div className="iv2-container">
        <div className="iv2-audit-form-head">
          <p className="iv2-section-eyebrow">Business Systems Audit</p>
          <h1 className="iv2-audit-form-heading" data-testid="iv2-audit-heading">{t.heading}</h1>
          <p className="iv2-audit-form-intro">{t.intro}</p>
          <p className="iv2-audit-form-note">{t.required_note}</p>
        </div>

        {hasErrors && (
          <div
            className="iv2-audit-error-summary"
            role="alert"
            aria-live="assertive"
            data-testid="iv2-audit-error-summary"
          >
            {t.errors.summary}
          </div>
        )}
        {status === 'error' && (
          <div className="iv2-audit-error-summary" role="alert" data-testid="iv2-audit-server-error">
            {error || t.errors.generic}
          </div>
        )}

        <div className="iv2-audit-fieldset">
          <FormFieldV2 id="contact_name" label={t.labels.contact_name} required autoComplete="name"
            value={values.contact_name} onChange={setField('contact_name')} error={errors.contact_name} />
          <FormFieldV2 id="business_name" label={t.labels.business_name} required autoComplete="organization"
            value={values.business_name} onChange={setField('business_name')} error={errors.business_name} />
          <FormFieldV2 id="email" label={t.labels.email} required type="email" autoComplete="email" inputMode="email"
            value={values.email} onChange={setField('email')} error={errors.email} />
          <FormFieldV2 id="phone" label={t.labels.phone} type="tel" autoComplete="tel" inputMode="tel"
            value={values.phone} onChange={setField('phone')} />
          <FormFieldV2 id="role" label={t.labels.role} autoComplete="organization-title"
            value={values.role} onChange={setField('role')} />
          <FormFieldV2 as="select" id="preferred_language" label={t.labels.preferred_language} required
            value={values.preferred_language} onChange={setField('preferred_language')}>
            <option value="en">{t.labels.lang_en}</option>
            <option value="es">{t.labels.lang_es}</option>
          </FormFieldV2>

          <FormFieldV2 as="textarea" id="primary_challenge" label={t.labels.primary_challenge} required rows={4}
            value={values.primary_challenge} onChange={setField('primary_challenge')} error={errors.primary_challenge} />

          <FormFieldV2 as="select" id="employees" label={t.labels.employees}
            value={values.employees} onChange={setField('employees')}>
            <option value="">{t.labels.pick}</option>
            <option value="<10">{t.labels.emp_small}</option>
            <option value="10-50">{t.labels.emp_mid}</option>
            <option value="50-200">{t.labels.emp_large}</option>
            <option value="200+">{t.labels.emp_enterprise}</option>
          </FormFieldV2>
          <FormFieldV2 id="locations" label={t.labels.locations} type="number" inputMode="numeric" min="0"
            value={values.locations} onChange={setField('locations')} />
          <FormFieldV2 as="textarea" id="current_tools" label={t.labels.current_tools} rows={2}
            value={values.current_tools} onChange={setField('current_tools')} />
          <FormFieldV2 as="textarea" id="business_impact" label={t.labels.business_impact} rows={2}
            value={values.business_impact} onChange={setField('business_impact')} />

          <FormFieldV2 as="select" id="bilingual_required" label={t.labels.bilingual_required}
            value={values.bilingual_required} onChange={setField('bilingual_required')}>
            <option value="">{t.labels.pick}</option>
            <option value="yes">{t.labels.bilingual_yes}</option>
            <option value="no">{t.labels.bilingual_no}</option>
            <option value="unsure">{t.labels.bilingual_unsure}</option>
          </FormFieldV2>
          <FormFieldV2 as="select" id="timeline" label={t.labels.timeline}
            value={values.timeline} onChange={setField('timeline')}>
            <option value="">{t.labels.pick}</option>
            <option value="< 30d">{t.labels.timeline_30}</option>
            <option value="30-90d">{t.labels.timeline_90}</option>
            <option value="90d+">{t.labels.timeline_later}</option>
            <option value="exploring">{t.labels.timeline_explore}</option>
          </FormFieldV2>
          <FormFieldV2 as="select" id="investment_range" label={t.labels.investment_range} hint={t.labels.investment_hint}
            value={values.investment_range} onChange={setField('investment_range')}>
            <option value="">{t.labels.pick}</option>
            <option value="under_2k">{t.labels.invest_a}</option>
            <option value="2k_5k">{t.labels.invest_b}</option>
            <option value="5k_15k">{t.labels.invest_c}</option>
            <option value="15k_plus">{t.labels.invest_d}</option>
            <option value="unsure">{t.labels.invest_none}</option>
          </FormFieldV2>
          <FormFieldV2 as="textarea" id="additional_context" label={t.labels.additional_context} rows={3}
            value={values.additional_context} onChange={setField('additional_context')} />
        </div>

        {/* Honeypot — hidden from users + AT via aria-hidden + inert-like styling */}
        <div className="iv2-honeypot" aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off"
                 value={values.website} onChange={setField('website')} />
        </div>

        <div className="iv2-audit-consent">
          <label htmlFor="contact_consent" className="iv2-consent-label">
            <input
              id="contact_consent" type="checkbox"
              checked={values.contact_consent}
              onChange={setField('contact_consent')}
              aria-invalid={errors.contact_consent ? 'true' : 'false'}
              aria-describedby={errors.contact_consent ? 'contact_consent-error' : undefined}
              aria-required="true"
              data-testid="iv2-input-contact_consent"
            />
            <span>
              {t.labels.contact_consent}{' '}
              <a href="/privacy" className="iv2-consent-link" target="_blank" rel="noopener noreferrer">
                {t.labels.privacy}
              </a>
            </span>
          </label>
          {errors.contact_consent && (
            <span id="contact_consent-error" className="iv2-field-error" role="alert">
              {errors.contact_consent}
            </span>
          )}
        </div>

        <button
          type="submit"
          className="iv2-btn iv2-btn-primary iv2-audit-submit"
          disabled={pending}
          data-testid="iv2-audit-submit"
        >
          {pending ? t.labels.submitting : t.labels.submit}
        </button>

        <button type="button" onClick={reset} className="iv2-audit-hidden-reset" hidden aria-hidden="true">
          reset
        </button>
      </div>
    </form>
  );
};

export default AuditFormV2;
